import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { is_empty } from "features/Common/utils/common.utils";
import { useState } from "react";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { uploadAndAddDocsOrImgToMediaLib } from "./utils/InitiateDisputeModal.utils";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { logError } from "utils/error";
import { DISPUTE_APIS } from "ui/pages/transactions/DisputeHistory/constants/disputes.apis";
import { useRefresh } from "react-admin";
import { useDisputeModal } from "./store/useDisputeModal";
import { initiateJuspay } from "features/Payment/utils/Payment.utils";
import { TRANS_CONSTANTS } from "../../transactions.constants";
import { useWallets } from "features/Account/modules/Wallets/utils/useWallets";
import { useWalletStore } from "features/Account/modules/Wallets/store/WalletStore";
import { walletKeys } from "features/Account/modules/Wallets/constants/Wallets.constants";
import { useDisputeHistory } from "ui/pages/transactions/DisputeHistory/utils/useDisputeHistory";
import { URL_PARAMS_KEYS } from "features/Common/modules/Urls&Routing/constants/URL.constant";

export const useInitiateDispute = () => {
  const { notify } = useNotifications();

  const { refetchRaisedDisputes } = useDisputeHistory();
  const files = useDisputeModal((state) => state.files);
  const setFiles = useDisputeModal((state) => state.setFiles);
  const setisOpenInitiateDisputeModal = useDisputeModal(
    (state) => state.setisOpenInitiateDisputeModal
  );
  const setIsOpenDisputeConfirmationModal = useDisputeModal(
    (state) => state.setIsOpenDisputeConfirmationModal
  );
  const setIsOpenDisputePaymentModal = useDisputeModal(
    (state) => state.setIsOpenDisputePaymentModal
  );
  const { walletBalance } = useWallets({
    isFetchWalletBalanceEnabled: true,
  });
  const isPayViaWallet = useWalletStore((state) => state.isPayViaWallet);
  const setIsPayViaWallet = useWalletStore((state) => state.setIsPayViaWallet);
  const [loading, setLoading] = useState(false);

  const refresh = useRefresh();

  const { mutate: disputesUploadDocuments } = useCustomMutation({
    url: DISPUTE_APIS.dispute_upload_documents,
    onSuccess: () => {},
    onError: () => {},
  });

  const { mutate: disputeConfirmation } = useCustomMutation({
    url: DISPUTE_APIS.dispute_confirmation,
    onSuccess: () => {},
    onError: () => {},
  });

  const { mutate: disputePayments } = useCustomMutation({
    url: DISPUTE_APIS.dispute_payment,
    onSuccess: () => {},
    onError: () => {},
  });

  const onChangeFileInput = async (e) => {
    setLoading(true);
    const updatedFiles = [...e.target.files];
    await uploadAndAddDocsOrImgToMediaLib({
      files: updatedFiles,
      existingFiles: files,
      notify,
      updateFiles: setFiles,
      setLoading,
      callInitiatedFrom:
        "src/ui/pages/transactions/AllTransaction/components/InitiateDisputeModal/InitiateDisputeModal.jsx",
    });
  };

  const handleDisputeConfirmation = ({ record }) => {
    const handleSuccess = () => {
      refresh();
      refetchRaisedDisputes();
      setIsOpenDisputeConfirmationModal(false);
      notify(
        "Confirmation updated successfully.",
        notification_color_keys.success
      );
    };

    const handleError = (error) => {
      notify(
        error?.response?.data?.message || SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
      logError({
        error,
        customErrorMessage: "Error while dispute confirmation",
        when: "calling handleDisputeConfirmation",
        occuredAt:
          "src/ui/pages/transactions/AllTransaction/components/InitiateDisputeModal/useInitiateDispute.jsx",
        severity: "blocker",
      });
      throw new Error(`Error dispute confirmation: ${error}`);
    };

    disputeConfirmation(
      {
        transaction_uuid: record?.transaction_uuid
          ? record?.transaction_uuid
          : record?.uuid,
      },
      { onSuccess: handleSuccess, onError: handleError }
    );
  };

  const handleDisputePayments = ({ record }) => {
    const transactionId = record?.transaction_uuid
      ? record?.transaction_uuid
      : record?.uuid;
    const payload = {
      transaction_uuid: transactionId,
      amount: record?.dispute_amount,
      return_url: `${window.location.origin}/#/payments/disputes/history?${URL_PARAMS_KEYS.transactionId}=${transactionId}`,
    };
    if (isPayViaWallet) {
      payload[walletKeys.walletBalance] =
        walletBalance?.[walletKeys.walletBalance];
    }
    const handleSuccess = (response) => {
      if (response?.gateway === TRANS_CONSTANTS.free) {
        setIsPayViaWallet(false);
        setIsOpenDisputePaymentModal(false);
        notify("Payment successfully.", notification_color_keys.success);
        refresh();
        setisOpenInitiateDisputeModal(true);
      } else {
        if (!is_empty(response?.order)) {
          notify(
            "Payment initiated successfully.",
            notification_color_keys.success
          );
          initiateJuspay(response.order);
        } else {
          notify(SOMETHING_WENT_WRONG, notification_color_keys.error);
        }
      }
    };

    const handleError = (error) => {
      notify(
        error?.response?.data?.message || SOMETHING_WENT_WRONG,
        notification_color_keys.error
      );
      logError({
        error,
        customErrorMessage: "Error while dispute confirmation",
        when: "calling handleDisputeConfirmation",
        occuredAt:
          "src/ui/pages/transactions/AllTransaction/components/InitiateDisputeModal/useInitiateDispute.jsx",
        severity: "blocker",
      });
      throw new Error(`Error dispute confirmation: ${error}`);
    };

    disputePayments(
      { ...payload },
      { onSuccess: handleSuccess, onError: handleError }
    );
  };

  const handleDelete = (index) => {
    let updatedFiles = files;
    updatedFiles.splice(index, 1);
    setFiles([...updatedFiles]);
  };

  const handleClose = () => {
    setFiles([]);
    setisOpenInitiateDisputeModal(false);
  };

  const handleDisputeDocumentUpload = (record) => {
    if (!is_empty(files)) {
      const filesLinksSet = files.map((file) => {
        return file.fileLocation;
      });

      const handleSuccess = () => {
        refresh();
        refetchRaisedDisputes();
        handleClose();
        notify(
          "Document uploded successfully.",
          notification_color_keys.success
        );
      };

      const handleError = (error) => {
        notify(
          error?.response?.data?.message || SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
        logError({
          error,
          customErrorMessage: "Error while dispute document uploading",
          when: "calling handleDisputeDocumentUpload",
          occuredAt:
            "src/ui/pages/transactions/AllTransaction/components/InitiateDisputeModal/useInitiateDispute.jsx",
          severity: "blocker",
        });
        throw new Error(`Error dispute document uploading: ${error}`);
      };

      disputesUploadDocuments(
        {
          transaction_uuid: record?.transaction_uuid
            ? record?.transaction_uuid
            : record?.uuid,
          file_urls: filesLinksSet,
        },
        { onSuccess: handleSuccess, onError: handleError }
      );
    }
  };

  return {
    loading,
    onChangeFileInput,
    handleDelete,
    handleClose,
    handleDisputeDocumentUpload,
    handleDisputeConfirmation,
    handleDisputePayments,
  };
};
